import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderIcon, DocumentTextIcon, ArrowUturnLeftIcon, CheckIcon, ArrowPathIcon, ExclamationTriangleIcon, XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/apiClient';
import { WizardComponentProps } from '../../types';

const allowed = new Set(['csv', 'xlsx', 'xls']);

type SpItem = { id: string; name: string; type: 'file' | 'folder'; fileType: string; size?: number; path: string };

type SelectionItem = {
  id: string;
  name: string;
  path: string;
  size?: number;
  fileType: string;
  columns: string[];
  lastModifiedDateTime?: string;
  eTag?: string;
};

const FileUploader: React.FC<WizardComponentProps> = ({ isEmbedded }) => {
  const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();
  const queryClient = useQueryClient();

  const [path, setPath] = useState<string>('');
  const [selected, setSelected] = useState<Record<string, SelectionItem>>({});
  const [checking, setChecking] = useState(false);
  const [checks, setChecks] = useState<Record<string, { id: string; status: 'ok' | 'missing' | 'updated' | 'error'; lastModifiedDateTime?: string }>>({});

  const { data: browse, isLoading, refetch } = useQuery({
    queryKey: ['sp-browse', customerId, phaseId, path],
    queryFn: () => apiClient.browseSharePoint(customerId!, phaseId!, path || undefined),
    enabled: !!(customerId && phaseId)
  });

  useEffect(() => {
    if (browse?.requiresAuth && browse?.authorizeUrl) {
      toast.loading('Redirecting to SharePoint authentication...', { duration: 2000 });
      setTimeout(() => {
        const url = new URL(browse.authorizeUrl);
        const state = JSON.parse(url.searchParams.get('state') || '{}');
        state.returnUrl = window.location.pathname + window.location.search;
        url.searchParams.set('state', JSON.stringify(state));
        window.location.href = url.toString();
      }, 500);
    }
  }, [browse]);

  const { data: existingSelection } = useQuery({
    queryKey: ['sp-selection', customerId, phaseId],
    queryFn: () => apiClient.getSharePointSelection(customerId!, phaseId!),
    enabled: !!(customerId && phaseId)
  });

  useEffect(() => {
    if (existingSelection && Array.isArray(existingSelection)) {
      const map: Record<string, SelectionItem> = {};
      existingSelection.forEach((f: SelectionItem) => { map[f.id] = f; });
      setSelected(map);
    }
  }, [existingSelection]);

  const saveMutation = useMutation({
    mutationFn: (files: SelectionItem[]) => apiClient.saveSharePointSelection(customerId!, phaseId!, files),
    onSuccess: async () => {
      toast.success('Selection saved');
      await queryClient.invalidateQueries({ queryKey: ['sp-selection', customerId, phaseId] });
      // Also invalidate the query key used by mapping components (EmployeeIdMappingStep, MappingCanvasV3)
      await queryClient.invalidateQueries({ queryKey: ['sharepointSelection', customerId, phaseId] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message || 'Save failed')
  });

  const inspect = async (item: SpItem) => {
    try {
      const res = await apiClient.inspectSharePointFile(customerId!, phaseId!, item.id);
      if (res?.requiresAuth && res?.authorizeUrl) {
        toast.loading('Redirecting to SharePoint authentication...');
        const url = new URL(res.authorizeUrl);
        const state = JSON.parse(url.searchParams.get('state') || '{}');
        state.returnUrl = window.location.pathname + window.location.search;
        url.searchParams.set('state', JSON.stringify(state));
        window.location.href = url.toString();
        return;
      }
      const columns: string[] = Array.isArray(res?.columns) ? res.columns : [];
      if (!columns.length) {
        toast.error('No headers detected');
        return;
      }
      setSelected(prev => ({
        ...prev,
        [item.id]: {
          id: item.id,
          name: item.name,
          path: item.path,
          size: res.size || item.size,
          fileType: item.fileType,
          columns,
          lastModifiedDateTime: res.lastModifiedDateTime,
          eTag: res.eTag
        }
      }));
      toast.success(`Detected ${columns.length} columns`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Inspect failed');
    }
  };

  const checkStatus = async () => {
    if (!customerId || !phaseId) return;
    setChecking(true);
    try {
      const results = await apiClient.checkSharePointSelection(customerId, phaseId);
      const map: typeof checks = {};
      if (Array.isArray(results)) {
        results.forEach((r: any) => map[r.id] = r);
      }
      setChecks(map);

      // Check if any items were missing (and thus removed by backend) or recovered
      const hasChanges = results.some((r: any) => r.status === 'missing' || r.recovered);
      if (hasChanges) {
        toast.success('File list updated based on check results');
        await queryClient.invalidateQueries({ queryKey: ['sp-selection', customerId, phaseId] });
        await queryClient.invalidateQueries({ queryKey: ['sharepointSelection', customerId, phaseId] });
      } else {
        toast.success('Status check complete');
      }
    } catch (e: any) {
      toast.error('Check failed');
    } finally {
      setChecking(false);
    }
  };

  const remove = (id: string) => {
    setSelected(prev => { const copy = { ...prev }; delete copy[id]; return copy; });
  };

  const crumbs = useMemo(() => {
    const parts = path.split('/').filter(Boolean);
    const acc: { name: string; to: string }[] = [{ name: 'root', to: '' }];
    let cur = '';
    for (const p of parts) {
      cur += `/${p}`;
      acc.push({ name: p, to: cur });
    }
    return acc;
  }, [path]);

  return (
    <div className="max-w-6xl mx-auto">
      {!isEmbedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">SharePoint Files</h1>
          <p className="text-gray-600 mt-2">Browse your SharePoint site and select CSV/Excel files for mapping</p>
        </div>
      )}

      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm mb-4">
        {crumbs.map((c, idx) => (
          <span key={c.to} className="flex items-center gap-2">
            {idx > 0 && <span className="text-gray-400">/</span>}
            <button className="text-blue-600 hover:underline" onClick={() => setPath(c.to)}>{c.name || 'root'}</button>
          </span>
        ))}
      </div>

      {/* Browser */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(browse?.items || []).map((item: SpItem) => (
              <div key={item.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center gap-3">
                  {item.type === 'folder' ? (
                    <FolderIcon className="w-6 h-6 text-yellow-600" />
                  ) : (
                    <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                  )}
                  <div>
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs text-gray-500">{item.type === 'folder' ? 'Folder' : item.fileType.toUpperCase()} · {item.path}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {item.type === 'folder' ? (
                    <button className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200" onClick={() => setPath(item.path)}>Open</button>
                  ) : (
                    <>
                      {allowed.has(item.fileType) && (
                        <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700" onClick={() => inspect(item)}>Inspect</button>
                      )}
                      {selected[item.id] ? (
                        <button className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded" onClick={() => remove(item.id)}>
                          <CheckIcon className="w-4 h-4 inline mr-1" />Selected
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selection */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Selected Files</h3>
          <div className="flex gap-2">
            <button
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-2"
              onClick={checkStatus}
              disabled={checking || Object.keys(selected).length === 0}
            >
              {checking ? (
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
              ) : (
                <ArrowPathIcon className="w-5 h-5" />
              )}
              Check Status
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={() => saveMutation.mutate(Object.values(selected))}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Selection'}
            </button>
          </div>
        </div>
        {Object.values(selected).length === 0 ? (
          <div className="text-sm text-gray-500">No files selected yet</div>
        ) : (
          <div className="space-y-3">
            {Object.values(selected).map(sel => {
              const status = checks[sel.id];
              return (
                <div key={sel.id} className={`p-3 border rounded ${status?.status === 'missing' ? 'bg-red-50 border-red-200' : status?.status === 'updated' ? 'bg-yellow-50 border-yellow-200' : ''}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {sel.name}
                        {status?.status === 'ok' && <CheckCircleIcon className="w-4 h-4 text-green-500" title="File verified" />}
                        {status?.status === 'missing' && <XCircleIcon className="w-4 h-4 text-red-500" title="File missing on SharePoint" />}
                        {status?.status === 'updated' && <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" title="File updated on SharePoint" />}
                      </div>
                      <div className="text-xs text-gray-500">{sel.path}</div>
                      {status?.status === 'missing' && <div className="text-xs text-red-600 font-medium mt-1">File not found on SharePoint</div>}
                      {status?.status === 'updated' && (
                        <div className="text-xs text-yellow-700 font-medium mt-1">
                          File has been modified since last inspect.
                          <button className="underline ml-1" onClick={() => inspect({ ...sel, type: 'file' } as any)}>Re-inspect</button>
                        </div>
                      )}
                    </div>
                    <button className="text-red-500 hover:text-red-700" onClick={() => remove(sel.id)}>
                      <XCircleIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="mt-2">
                    <div className="text-sm font-medium mb-1">Columns ({sel.columns.length})</div>
                    <div className="flex flex-wrap gap-1">
                      {sel.columns.map((c) => (
                        <span key={c} className="px-2 py-0.5 text-xs bg-gray-100 rounded">{c}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploader;
